/**
 * Hindi copy for the shared location-search components.
 * `city` must already contain its Hindi display name. Vehicle names, seat
 * notation, amounts and phone numbers are owned by the existing application.
 */

const hindiCity = (city) =>
  typeof city === 'string' && city.trim() ? city.trim() : 'सतना';

/**
 * Keep the English builder's argument contract. Optional location/guidance
 * content is supplied through getLocalizedLocation and is already in Hindi.
 * Each service key maps to a plain-text paragraph.
 */
export function buildLocationSearchCopyHi(city, location) {
  const name = hindiCity(city);
  const highlight = location?.highlights?.[0];
  const pickup = location?.pickupInfo;
  const connections = (location?.connections || []).map(item => item.label).slice(0, 2);
  return {
    'taxi-services': (highlight ? highlight.replace(/[।.!?]$/, '') + '। ' : 'सतना से स्थानीय यात्रा, स्टेशन ट्रांसफर या शहर से बाहर की यात्रा की योजना बनाएँ। ') + 'यात्रा की तारीख, पिकअप का समय और एकतरफ़ा या वापसी सहित यात्रा की जरूरत बताएँ।',
    'car-hire': name + ' की यात्रा के लिए सेडान, SUV या MPV की तुलना यात्रियों, सामान और रास्ते के ठहराव के अनुसार करें। पसंद का मॉडल और यात्रा की तारीख भेजकर उपलब्धता की पुष्टि कराएँ।',
    'car-rental': 'चालक सहित स्थानीय कार किराया 120 KM / 12 घंटे के लिए है और ईंधन अतिरिक्त है। शहर से बाहर के किराये में ईंधन शामिल है और प्रतिदिन न्यूनतम 300 KM लागू हैं। किराया पूछते समय यात्रा के सभी दिन और जरूरी घंटे बताएँ।',
    'cab-booking': (pickup || 'सतना रेलवे स्टेशन, होटल या घर से पिकअप के लिए मिलने की सही जगह बताएँ।') + ' पिकअप की तारीख, समय और आगे का गंतव्य भेजें; ट्रेन से आने पर ट्रेन का विवरण भी दें।',
    'tempo-traveller-on-rental': 'कुल 13, 17, 20, 21 और 26 सीटों के विकल्पों में एक चालक शामिल है। वाहन चुनने से पहले यात्रियों और सामान की संख्या बताएँ। 9-सीटर की बैठने की व्यवस्था और किराये की पुष्टि अनुरोध पर अलग से की जाती है।',
    'tempo-traveller-on-hire': (connections.length ? 'क्या आपकी यात्रा में ' + connections.join(' या ') + ' भी शामिल हैं? ' : 'शादी, तीर्थयात्रा या कई दिनों की समूह यात्रा की योजना है? ') + 'सभी पिकअप, ठहराव, इंतजार और रात रुकने की जगहें एक साथ भेजें, ताकि पूरी यात्रा का किराया बताया जा सके।',
    'tempo-traveller-on-booking': 'जाने और लौटने की तारीख, यात्रियों की संख्या और अंतिम ड्रॉप बताएँ। बुकिंग पक्की होने से पहले सतना की टीम वाहन, यात्री सीटों, पिकअप, पूरे किराये और लागू शर्तों की पुष्टि करती है।',
  };
}
/** Practical pickup, dispatch, driver and passenger-capacity questions. */
export function getLocalBookingFaqsHi(city, location) {
  const name = hindiCity(city);
  return [
    {
      q: 'क्या ' + name + ' में मेरे पते के पास टैक्सी पिकअप की व्यवस्था हो सकती है?',
      a: (location?.pickupInfo || 'सतना रेलवे स्टेशन, होटल, कार्यालय या घर से पिकअप के लिए सही पता या मिलने की जगह बताएँ।') + ' यात्रा की तारीख, पिकअप का समय और आगे का गंतव्य भेजें। स्टेशन से आने पर ट्रेन नंबर भी दें, ताकि सही स्टेशन पर पिकअप की पुष्टि हो सके।',
    },
    {
      q: name + ' की यात्रा के लिए वाहन की व्यवस्था कैसे होती है?',
      a: (location?.dispatch || 'हम सतना से सेवा की व्यवस्था करते हैं। पूरा मार्ग और यात्रा की तारीख पहले भेजें, ताकि टीम वाहन की उपलब्धता, पिकअप और पूरे किराये की पुष्टि कर सके।') + ' केवल पूछताछ भेजने से बुकिंग पक्की नहीं होती।',
    },
    {
      q: 'क्या ' + name + ' में किराए की कार के साथ ड्राइवर शामिल है?',
      a: 'हमारे प्रकाशित कार किराये के विकल्पों में ड्राइवर शामिल है। यात्रियों, सामान, यात्रा के दिनों और जरूरी घंटों की जानकारी दें। पूरा किराया पक्का करने से पहले स्थानीय या शहर से बाहर की यात्रा के ईंधन की शर्तें, न्यूनतम किलोमीटर और अतिरिक्त खर्च टीम से स्पष्ट कर लें।',
    },
    {
      q: name + ' की यात्रा के लिए Tempo Traveller में कितने यात्री बैठ सकते हैं?',
      a: 'प्रकाशित कुल 13, 17, 20, 21 और 26 सीटों में एक ड्राइवर शामिल है; इनमें क्रमशः 12, 16, 19, 20 और 25 यात्रियों की सीटें हैं। वाहन चुनते समय सामान की जरूरत बताएँ। 9-सीटर की बैठने की व्यवस्था और यात्री क्षमता की अलग से पुष्टि जरूरी है; इसका किराया अनुरोध पर बताया जाता है।',
    },
  ];
}
/**
 * Plain-text UI labels/templates. Replace the named placeholders using the
 * application's existing formatter; preserve supplied numeric values exactly.
 */
export const HI_TRAVEL_DETAILS = Object.freeze({
  localTaxiService: '{city} में स्थानीय टैक्सी सेवा',
  railwayStationTaxi: '{city} में रेलवे स्टेशन पिकअप और ड्रॉप के लिए टैक्सी',
  localSightseeingCab: '{city} में दर्शनीय स्थलों की सैर के लिए स्थानीय कैब',
  sightseeingInstructions:
    'आप {city} में जिन दर्शनीय स्थलों पर जाना चाहते हैं, उनके नाम बताएँ। पिकअप का स्थान, यात्रा की तारीख, रास्ते के ठहराव और सैर के लिए जरूरी समय साझा करें।',
  outstationTaxiService: '{city} से बाहर की यात्रा के लिए टैक्सी सेवा',
  oneWayReturnItinerary:
    'एकतरफ़ा यात्रा या वापसी सहित अपना पूरा कार्यक्रम बताएँ। गंतव्य, तारीखों, रास्ते के ठहराव और रात रुकने की जरूरत की जानकारी भी दें।',
  cityToDestinationTaxiEnquiry:
    '{city} से {destination} के लिए टैक्सी की पूछताछ करें',
  cityToDestinationTempoEnquiry:
    '{city} से {destination} के लिए Tempo Traveller की पूछताछ करें',
  travelGuidanceLink: '{city} की यात्रा के लिए उपयोगी जानकारी',
  taxiFarePerKm: '{city} में टैक्सी का प्रति किमी किराया',
  publishedTariffGuidance:
    'वाहनों की प्रकाशित किराया सूची में दी गई दरें देखें। अपने मार्ग, यात्रा की तारीख, वाहन और यात्रा की अवधि के अनुसार अंतिम किराये की पुष्टि कराएँ।',
  tempoTravellerRatePerKm: '{city} में Tempo Traveller की प्रति किमी दर',
  rateConditions:
    'किराया पूछते समय प्रतिदिन न्यूनतम किलोमीटर, ड्राइवर के भत्ते, रात्रि शुल्क, अतिरिक्त घंटे, टोल, पार्किंग और करों की शर्तें स्पष्ट कर लें। यह भी पूछें कि बताए गए किराये में कौन-से शुल्क शामिल हैं।',
  comparePublishedVehicleRates: 'वाहनों की प्रकाशित दरों की तुलना करें',
  rentTempoTraveller: 'Tempo Traveller किराये पर लें',
  seatingStatement13:
    '13 सीटों में 12 यात्रियों और 1 ड्राइवर की जगह शामिल है।',
  seatOptionLink: '{seats} सीटर Tempo Traveller के बारे में पूछें',
  pickupQuestions: 'पिकअप कहाँ से चाहिए?',
  exactLocationGuidance:
    'सेवा का आधार सतना है। {city} में पिकअप की सही जगह, गंतव्य, यात्रा की तारीख और समय बताएँ, ताकि वाहन की उपलब्धता और किराया जाँचा जा सके।',
  requestPickupAvailability: 'पिकअप की उपलब्धता पूछें',
});
